import { useParams, Navigate, Link } from "react-router-dom";
import { Check, Minus, Calendar, Info } from "lucide-react";

import "../../styles/marketing.css";
import Seo, { breadcrumbJsonLd, faqJsonLd, SITE_URL } from "../../components/marketing/Seo";
import MarketingNav from "../../components/marketing/MarketingNav";
import MarketingFooter from "../../components/marketing/MarketingFooter";
import Breadcrumbs from "../../components/marketing/Breadcrumbs";
import CtaBanner from "../../components/marketing/CtaBanner";
import Reveal from "../../components/marketing/Reveal";
import { getComparison, COMPARE_DISCLAIMER } from "../../data/compareData";

export default function ComparePage() {
  const { slug } = useParams();
  const comp = getComparison(slug);
  if (!comp) return <Navigate to="/compare" replace />;

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Compare", path: "/compare" },
    { name: `vs ${comp.competitor}`, path: `/compare/${comp.slug}` },
  ];

  return (
    <div className="mk min-h-screen overflow-x-hidden" data-testid={`compare-page-${comp.slug}`}>
      <Seo
        title={comp.seoTitle}
        description={comp.seoDescription}
        path={`/compare/${comp.slug}`}
        image={`${SITE_URL}/marketing/og/og-compare.jpg`}
        jsonLd={[breadcrumbJsonLd(crumbs), faqJsonLd(comp.faqs)]}
      />
      <MarketingNav />

      {/* Hero */}
      <section className="pt-24 md:pt-32 pb-12 bg-gradient-to-b from-blue-50/70 via-white to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <Breadcrumbs items={crumbs} />
          </div>
          <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[var(--mk-blue)] mb-4">Software Comparison</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.1] mb-5" data-testid="compare-h1">
            {comp.h1}
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed">{comp.intro}</p>
        </div>
      </section>

      {/* Comparison table */}
      <section className="pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-[0_10px_30px_-8px_rgba(15,23,42,0.08)]">
              <table className="w-full min-w-[560px] bg-white">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-4 px-5 text-[13px] font-semibold text-slate-500 uppercase tracking-wider">Capability</th>
                    <th className="py-4 px-4 text-center bg-[var(--mk-blue-light)]/50 w-[150px]">
                      <span className="text-[15px] font-bold text-[var(--mk-blue)]">Fieldz AI</span>
                    </th>
                    <th className="py-4 px-4 text-center text-[14px] font-semibold text-slate-600 w-[220px]">{comp.competitor}</th>
                  </tr>
                </thead>
                <tbody>
                  {comp.rows.map((r, i) => (
                    <tr key={r.label} className={`border-b border-slate-100 last:border-0 ${i % 2 === 1 ? "bg-slate-50/50" : ""}`}>
                      <td className="py-4 px-5 text-[14.5px] text-slate-700 font-medium">{r.label}</td>
                      <td className="py-4 px-4 text-center bg-[var(--mk-blue-light)]/30">
                        {r.fieldz === true ? <Check className="w-5 h-5 text-emerald-600 mx-auto" /> : <span className="text-[13px] text-slate-600 font-medium">{r.fieldz}</span>}
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className="inline-flex items-center gap-1 text-[13px] text-slate-500 font-medium">
                          {r.other === "Included" ? <Check className="w-4 h-4 text-slate-400" /> : <Minus className="w-3.5 h-3.5 text-slate-300" />}
                          {r.other}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
          <p className="flex items-start gap-2 text-[12.5px] text-slate-400 leading-relaxed mt-4" data-testid="compare-disclaimer">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            {COMPARE_DISCLAIMER}
          </p>
        </div>
      </section>

      {/* Differentiators */}
      <section className="py-16 md:py-20 bg-[var(--mk-bg-2)] border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 leading-[1.2]">
              Where Fieldz AI pulls ahead
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-5">
            {comp.differentiators.map((d, i) => (
              <Reveal key={d.title} delay={Math.min((i % 2) + 1, 2)}>
                <div className="h-full rounded-2xl border border-slate-200 bg-white p-6">
                  <h3 className="text-[17px] font-semibold text-slate-900 mb-2">{d.title}</h3>
                  <p className="text-[14.5px] text-slate-600 leading-relaxed">{d.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 text-center mb-10">Common questions</h2>
          <div className="space-y-4">
            {comp.faqs.map((f, i) => (
              <div key={f.q} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`compare-faq-${i}`}>
                <h3 className="text-slate-900 font-semibold mb-2 text-[15.5px]">{f.q}</h3>
                <p className="text-slate-600 text-[14.5px] leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/pricing" className="mk-btn mk-btn-outline" data-testid="compare-pricing-link">
              See Fieldz AI pricing <Calendar className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <CtaBanner
        title="Decide with your own data"
        body={`Run a week of real jobs through the free trial and compare it to ${comp.competitor} side by side.`}
      />
      <MarketingFooter />
    </div>
  );
}
